import { RfqListingTemplate as template } from './rfq-listing.template';
import { RfqListingStyles as styles } from './rfq-listing.styles';
import { customElement, FASTElement, observable } from '@microsoft/fast-element';
import { ZeroGridPro as zeroAgGrid } from '@genesislcap/foundation-zero-grid-pro';
import { updateArray } from '../../../../utils/dataserver';
import { logger, Message, SocketSubscription } from '@genesislcap/foundation-comms';
import { IRfqService } from '../../../../services';
import { CONTRACT_TYPE, VPPA_RFQ_STATUS } from '../../../../utils';
import { IEnumService } from '../../../../services/enum.service';
import { IRfq } from '../../../../types';

const name = 'rfq-listing';

@customElement({
  name,
  template,
  styles,
})
export class RfqListing extends FASTElement {
  @IRfqService rfqService: IRfqService;
  @IEnumService enumService: IEnumService;

  @observable rfqGrid: zeroAgGrid;
  @observable rfqSubscription: SocketSubscription;
  @observable rfqList: Array<IRfq>;
  @observable rfqCriteria = '';
  @observable allowRfqSelection = false; // update to true when project is selected
  @observable sortActive: boolean = false;

  public selectedRfqs: Array<IRfq> = [];

  connectedCallback(): void {
    super.connectedCallback();

    const gridOptions = {
      onRowDoubleClicked: (row) => this.rfqService.openRfqDetailModal(row.data?.RFQ_ID),
      animateRows: true,
      suppressRowClickSelection: true,
      suppressBrowserResizeObserver: true,
      defaultColDef: {
        resizable: true,
        wrapHeaderText: true,
        autoHeaderHeight: true,
      },
      onRowDragMove: (params) => this.RowDragMove(params),
      onRowSelected: (params) => this.rowSelected(params),
      getRowNodeId: (row: any) => row.RFQ_ID,
      rowClass: 'transparent-background',
      scrollbarWidth: 10,
      sideBar: {
        toolPanels: [
          {
            id: 'columns',
            labelDefault: 'Columns',
            labelKey: 'columns',
            iconKey: 'columns',
            toolPanel: 'agColumnsToolPanel',
            toolPanelParams: {
              suppressRowGroups: true,
              suppressValues: true,
              suppressPivotMode: true,
            },
          },
        ],
      },
    };

    this.rfqGrid.gridOptions = gridOptions;
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
  }

  public onGridReady() {
    this.setRfqListingCriteria('');
  }

  public setAllowRfqSelection(allowRfqSelection: boolean) {
    this.allowRfqSelection = allowRfqSelection;
  }

  public RowDragMove(event: any) {
    function moveInArray(arr, fromIndex, toIndex) {
      const element = arr[fromIndex];
      arr.splice(fromIndex, 1);
      arr.splice(toIndex, 0, element);
    }

    if (event.node !== event.overNode) {
      const movingData = event.node.data;
      const overData = event.overNode.data;

      const fromIndex = this.rfqList.indexOf(movingData);
      const toIndex = this.rfqList.indexOf(overData);

      const newStore = this.rfqList.slice();
      moveInArray(newStore, fromIndex, toIndex);

      this.rfqList = newStore;
      this.rfqGrid.gridApi.setRowData(newStore);
      this.rfqGrid.gridApi.clearFocusedCell();
      this.selectedRfqs = this.rfqGrid.gridApi.getSelectedRows().map((elm) => elm);
      this.$emit('rfqSelected', this.selectedRfqs);
    }
  }

  public rowSelected(event: any) {
    if (event && event.node) {
      if (event.node.selected) {
        if (!this.selectedRfqs.find((elm) => elm.RFQ_ID === event.data.RFQ_ID))
          this.selectedRfqs.push(event.data);
      } else {
        this.selectedRfqs = this.selectedRfqs.filter((elm) => elm.RFQ_ID !== event.data.RFQ_ID);
      }
      this.$emit('rfqSelected', this.selectedRfqs);
    }
  }

  public setRfqListingCriteria(rfqCriteria: string) {
    let resultList = [];
    this.rfqList = [];
    this.rfqGrid.gridApi.setFilterModel(null);
    this.rfqGrid.gridApi.onFilterChanged();
    this.rfqSubscription?.unsubscribe();
    if (!rfqCriteria) {
      rfqCriteria = `RFQ_STATUS == '${VPPA_RFQ_STATUS.ACTIVE}' || RFQ_STATUS == '${VPPA_RFQ_STATUS.EXISTING_INTEREST}'`;
    }

    rfqCriteria =
      '(' +
      rfqCriteria +
      `) && (CONTRACT_TYPE !== '${CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED}')`;

    this.rfqSubscription = this.rfqService.getAllRfqs(rfqCriteria).subscribe({
      next: (streamMessage: Message) => {
        resultList = streamMessage.ROW ?? null;
        if (resultList) {
          this.rfqList = updateArray(resultList, this.rfqList);
        } else {
          const resultERROR = streamMessage.DETAILS?.ERROR;
          logger.error(resultERROR);
        }
        this.rfqGrid.gridApi.setRowData(this.rfqList);
      },
      error: (error: any) => {
        logger.error(error);
      },
    });
  }

  public clearSelectedRfqs() {
    this.selectedRfqs = [];
    this.$emit('rfqSelected', this.selectedRfqs);
  }
}
